import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
} from "../../../../context/ui-feedback/action";
import { useSearchWorkers } from "../../../../api/users-api";
import { Loading } from "../../../common/loading/loading";

export const PrimaryWorkerSelect = ({
  exludedWorkers,
  primaryWorker,
  isValid,
  isInvalid,
  disabled,
  handlePrimaryWorkerSelect,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const searchWorkers = useSearchWorkers();

  const { uiFeedbackDispatch } = useUiFeedback();

  const loadedUsers = useRef(null);
  const [allUsers, setAllUsers] = useState(null);
  const [users, setUsers] = useState(null);

  useEffect(() => {
    if (allUsers !== null) {
      const exludedWorkerIds = exludedWorkers.map((ew) => ew.id);
      setUsers(allUsers.filter((u) => !exludedWorkerIds.includes(u.id)));
    }
  }, [exludedWorkers, allUsers]);

  useEffect(() => {
    const loadUsers = async () => {
      createWaiting(uiFeedbackDispatch);
      try {
        await searchWorkers((data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            let userData = data.map((u) => {
              return {
                id: u.id,
                name: u.name,
                user: u,
              };
            });

            if (
              primaryWorker?.id &&
              !data.find((u) => u.id === primaryWorker.id)
            ) {
              userData = [
                {
                  id: primaryWorker.id,
                  name: primaryWorker.name,
                  user: primaryWorker,
                },
                ...userData,
              ];
            }
            userData.sort((u1, u2) => u1.name.localeCompare(u2.name));

            setAllUsers(userData);
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.editserviceworksheet.exception.loaduser.title",
            }),
            intl.formatMessage({
              id: "page.editserviceworksheet.exception.loaduser.description",
            }),
          );
        }
      } finally {
        closeWaiting(uiFeedbackDispatch);
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      loadedUsers.current === null &&
      users === null &&
      checkNotEmpy([searchWorkers, intl, navigate, uiFeedbackDispatch])
    ) {
      loadUsers();
      loadedUsers.current = new Date();
    }
  }, [
    loadedUsers,
    users,
    primaryWorker,
    searchWorkers,
    intl,
    navigate,
    uiFeedbackDispatch,
  ]);

  if (!users) {
    return <Loading />;
  }

  return (
    <Form.Select
      value={primaryWorker?.id ? primaryWorker?.id : 0}
      isValid={isValid}
      isInvalid={isInvalid}
      disabled={disabled}
      onChange={(event) => {
        handlePrimaryWorkerSelect(
          event.target.value === 0
            ? null
            : users.find((u) => u.id === event.target.value).user,
        );
      }}
    >
      {primaryWorker?.id ? null : (
        <option value="0">
          <FormattedMessage id="data.serviceworksheet.form.primaryworker.value.null" />
        </option>
      )}
      {users.map((user) => (
        <option key={user.id} value={user.id}>
          {user.name}
        </option>
      ))}
    </Form.Select>
  );
};

PrimaryWorkerSelect.propTypes = {
  exludedWorkers: PropTypes.array.isRequired,
  primaryWorker: PropTypes.object,
  isValid: PropTypes.bool.isRequired,
  isInvalid: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  handlePrimaryWorkerSelect: PropTypes.func.isRequired,
};
